import { BaseText, Headline1, Meta, theme } from 'aimo-ui';
import CrickleCutCardBackground from 'components/common/CrickleCutCardBackground';
import Icon from 'components/icons/Icon';
import { LoyaltyLogo } from 'components/services/LoyaltyLogo';
import { default as React } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking } from 'react-native';
import styled from 'styled-components/native';
import { StyledWrapper } from 'styles/ContainerStyles';
import { UnifiedPermit } from 'types/generatedSchemaTypes';
import { getLang } from 'utils/commonUtils';
import LoyaltyPermitTicketContent from './LoyaltyPermitTicketContent';

const PermitTitleContainer = styled.View<{ isLogoAvailable?: boolean }>`
  width: 100%;
  padding: ${(p) => (p.isLogoAvailable ? `0px` : `15px`)} 20px;
`;

const ChipCardContainer = styled.TouchableOpacity<{ backgroundColor?: string }>`
  width: 100%;
  background-color: ${theme.colors.gray50};
  border-radius: 10px;
  margin: 9px 0px 15px 0;
  height: ${50}px;
  justify-content: center;
`;

const ItemsContainer = styled.View<{
  isVerticalCenter?: boolean;
}>`
  flex-direction: row;
  justify-content: space-between;
  display: flex;
  padding: 0 20px;
`;

const LearnMoreText = styled(Meta)`
  font-weight: 500;
  margin: 4px 0 0 8px;
`;

const BookAndTextWrapper = styled.View`
  flex-direction: row;
  align-content: center;
  gap: 10px;
`;

const LoyaltyPermitItemTitle = ({
  permit,
  showDescription = false,
}: {
  permit: UnifiedPermit;
  showDescription: boolean;
}) => {
  const { t } = useTranslation();
  const { id, pmcName, pmcDescription, placeName } = permit;

  const description = pmcDescription
    ? pmcDescription[getLang()]
    : pmcDescription;

  const isLogoAvailable = !!permit?.logo;
  const customerUsage =
    permit?.usageType === 'CUSTOMER' ? t('permit.customerUsage') : '';

  const handlePress = () => {
    if (permit.link) {
      Linking.openURL(permit.link).catch((err) =>
        console.error('Failed to open loyalty URL:', err)
      );
    }
  };

  return (
    <PermitTitleContainer isLogoAvailable={isLogoAvailable}>
      <StyledWrapper center mb={5}>
        {isLogoAvailable && (
          <StyledWrapper center>
            <LoyaltyLogo
              width={'120px'}
              height={'120px'}
              uri={permit.logo as string}
            />
          </StyledWrapper>
        )}
        {pmcName && (
          <StyledWrapper center>
            <Headline1 center testID={`loyalty-permit-${id}-name`}>
              {pmcName}
            </Headline1>
          </StyledWrapper>
        )}
        {placeName && (
          <StyledWrapper center>
            <BaseText center testID={`loyalty-permit-${id}-place`}>
              {placeName || ''}
            </BaseText>
          </StyledWrapper>
        )}
        {showDescription && description && (
          <BaseText center>{description}</BaseText>
        )}
        {customerUsage && (
          <StyledWrapper center>
            <Meta center testID={`loyalty-permit-${id}-usage`}>
              {customerUsage}
            </Meta>
          </StyledWrapper>
        )}
      </StyledWrapper>
      {permit.link && (
        <ChipCardContainer onPress={handlePress}>
          <ItemsContainer>
            <BookAndTextWrapper>
              <Icon name="Book" size="m" />
              <LearnMoreText>{t('permit.learnMore')}</LearnMoreText>
            </BookAndTextWrapper>
            <Icon name="Open" size="m" />
          </ItemsContainer>
        </ChipCardContainer>
      )}
    </PermitTitleContainer>
  );
};

/*
Extended version show descriptions under the title and doesn't have the "View details" button
 */
export const LoyaltyPermitTicket = ({
  permit,
  cutBackgroundColor,
  extended = false,
}: {
  permit: UnifiedPermit;
  cutBackgroundColor?: string;
  extended?: boolean;
}) => {
  return (
    <StyledWrapper mb={20}>
      <CrickleCutCardBackground
        aboveCutView={
          <LoyaltyPermitItemTitle permit={permit} showDescription={extended} />
        }
        belowCutView={() => (
          <LoyaltyPermitTicketContent
            permit={permit}
            showDetailsButton={!extended}
          />
        )}
        backgroundColor={theme.colors.white}
        cutColor={cutBackgroundColor ?? theme.colors.gray100}
      />
    </StyledWrapper>
  );
};

export default LoyaltyPermitTicket;
